// CodeGate: shown when ?code= is missing or invalid.
// Single password field that validates against /api/health.

import React, { useState, FormEvent } from 'react';
import { t } from '../lib/i18n';
import type { Lang } from '../lib/i18n';

interface CodeGateProps {
  lang: Lang;
  onValidCode: (code: string) => void;
}

export function CodeGate({ lang, onValidCode }: CodeGateProps) {
  const [input, setInput] = useState('');
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!input.trim()) return;

    setLoading(true);
    setError(false);

    try {
      const resp = await fetch(`/api/health?code=${encodeURIComponent(input.trim())}`);
      if (resp.status === 200) {
        onValidCode(input.trim());
      } else {
        setError(true);
      }
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-neutral-50 flex items-center justify-center p-4">
      <div className="card p-8 w-full max-w-sm">
        {/* Logo mark */}
        <div className="mb-6 flex items-center gap-3">
          <svg viewBox="0 0 32 32" fill="none" className="w-8 h-8">
            <circle cx="16" cy="16" r="7" stroke="#34d399" strokeWidth="2" />
            <circle cx="16" cy="16" r="3" fill="#34d399" />
            <line x1="16" y1="4" x2="16" y2="9" stroke="#34d399" strokeWidth="2" strokeLinecap="round" />
          </svg>
          <span className="text-sm font-medium tracking-widest text-neutral-600 uppercase">
            {t('appTitle', lang)}
          </span>
        </div>

        <p className="text-neutral-600 text-sm mb-6">{t('codePrompt', lang)}</p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="sr-only" htmlFor="code-input">
              {t('enterCode', lang)}
            </label>
            <input
              id="code-input"
              type="password"
              autoComplete="off"
              placeholder={t('enterCode', lang)}
              value={input}
              onChange={(e) => {
                setInput(e.target.value);
                setError(false);
              }}
              className={`w-full bg-neutral-200 border rounded-md px-4 py-2.5 text-neutral-900 text-sm
                placeholder:text-neutral-500 focus:outline-none focus:ring-1
                ${error
                  ? 'border-rose-500 focus:ring-rose-500'
                  : 'border-neutral-300 focus:ring-emerald-500 focus:border-emerald-500'
                }`}
            />
            {error && (
              <p className="text-rose-600 text-xs mt-1.5">{t('invalidCode', lang)}</p>
            )}
          </div>

          <button
            type="submit"
            disabled={loading || !input.trim()}
            className="w-full bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed
                       text-white text-sm font-medium rounded-md py-2.5 transition-colors"
          >
            {loading ? t('loading', lang) : t('submit', lang)}
          </button>
        </form>
      </div>
    </div>
  );
}
